// callPerson.js — CALL ANYONE IN THE WORKSPACE (Nika 2026-10-23: "add calling
// mode into fly so you can call to anyone in workspace").
//
// One call: a private room named for the person, the person ADDED AS ITS
// MEMBER (by user id — a roster row may carry no email: a non-manager's
// roster omits them), this person joined into it on /meet, and, when the
// person's email is known, the join link in a DM. Membership is what lets the
// person see and join the room (and what their shell rings on:
// functions/incomingCall.js); the DM keeps the link in their chat.
//
// `opts.transcribe`: the call keeps a transcript — its summary then builds
// live during the call and waits on its end screen (meet's live recap). Fly's
// calls ask for it; the person popover's Call keeps the room default.
//
// Shared by the person popover's Call (components/PersonPopover.js) and Fly
// (the start screen's People, a "call <name>" ask). Returns the room id, or
// null when the room could not be made (the person is told).
//
// FRANK: self-contained (FA204); siblings through `this.call`.
export async function callPerson(who, opts) {
  const root = this.state && this.state.root
  if (!root || typeof root.update !== 'function') return null
  const person =
    (who && typeof who === 'object' ? who : null) ||
    (typeof who === 'string' && who ? this.call('findPerson', who) : null) ||
    null
  const email =
    (person && typeof person.email === 'string' && person.email) ||
    (typeof who === 'string' && who.includes('@') ? who : '') ||
    ''
  const userId = (person && (person.userId || person.id || person._id)) || null
  const label = (person && person.name) || email
  if (!label) return null
  const fail = () => {
    this.call('openNotification', {
      type: 'error',
      title: this.call('polyglot', 'workspace.person.callFailed'),
      message: this.call('polyglot', 'workspace.person.tryAgain')
    })
    return null
  }
  let roomId = null
  try {
    roomId = await this.call('createRoom', {
      name: String(this.call('polyglot', 'workspace.person.callRoomName')).replace('{name}', label),
      kind: 'room',
      visibility: 'private',
      transcribeEnabled: !!(opts && opts.transcribe)
    })
  } catch (err) {
    console.warn('[callPerson] createRoom failed', err && err.message)
  }
  if (!roomId) return fail()
  // The person is a member before anyone joins: their shell sees the room the
  // moment it is live.
  if (userId && String(userId) !== String(root.currentUserId || '')) {
    try {
      await this.call('addMeetMember', { roomId, userId })
    } catch (err) {
      console.warn('[callPerson] addMeetMember failed', err && err.message)
    }
  }
  // The call opens on /meet: a Messenger left open (a call started from a
  // person card in it) would cover the call, so it closes with the same write
  // (the rule of a link in a message, chat functions/chatLinkClick.js).
  root.update({
    activeRoomId: roomId,
    meetView: 'call',
    ...(root._chatDrawerOpen ? { _chatDrawerOpen: false } : {})
  })
  this.router('/meet', this.getRoot())
  if (typeof window !== 'undefined' && window.history) {
    // The room hash, on the address the route above just wrote: a hash-only
    // URL keeps its path, so the call keeps the tab's `/w/<org>/<workspace>`
    // (F2 — the bare `/meet#…` written here dropped it, and a reload opened
    // the call in the last-used workspace). A raw write on purpose, the stamp
    // meet's own call starters make: a second `this.router` cannot stamp it
    // (a newer navigation takes over the router's pending /meet mount while
    // the shell's onRouteExit motion runs), and a hash inside the route itself
    // is read by the meet page's mount-time seed (a cold `view=call` link
    // opens the join preview) whenever the page mounts at once.
    // frank-allow FA410
    window.history.replaceState(null, '', '#room=' + roomId + '&view=call')
  }
  this.call('connectCall', roomId)
  if (email) {
    try {
      // createChannel + sendMessage live in chat's lazy chunk: fold it first,
      // or the invite silently never sends.
      const folded = await this.call('ensureChatContext')
      if (folded !== false) {
        const channelId = await this.call('createChannel', { kind: 'dm', memberEmails: [email] })
        if (channelId) {
          const url = this.call('meetRoomUrl', root, roomId)
          const body = String(this.call('polyglot', 'workspace.person.callInviteBody')).replace(
            '{url}',
            url
          )
          await this.call('sendMessage', { channelId, body })
        }
      }
    } catch (err) {
      console.warn('[callPerson] invite message failed', err && err.message)
    }
  }
  return roomId
}
